import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useNavigate } from 'react-router-dom';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
}

/**
 * Navigates to the home page and scrolls to the contact form.
 * Added error handling to ensure the element exists before scrolling.
 */
const goToForm = (navigate: ReturnType<typeof useNavigate>) => {
  try {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('contact-form');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  } catch (error) {
    console.error('Failed to navigate to form:', error);
  }
};

export default function FAQ() {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleCTA = () => goToForm(navigate);

  const faqData: FAQItem[] = [
    {
      id: 1,
      question: '你們的系統模組只適合高單價的 B2B 產業嗎？',
      answer:
        '我們的 O2O 數位模組適用於任何需要「高意願名單收集、線上預約轉換、實體業務派單」的產業。無論是房地產、醫美診所、企業顧問或高價值零售，只要您的目標是獲取精準名單並建立內部防呆管理，這套系統都能為您量身導入專屬的商業邏輯。',
    },
    {
      id: 2,
      question: '系統模組導入與建置大約需要多久時間？',
      answer:
        '有別於傳統程式開發動輒數月且容易產生 Bug，我們採用企業級 SaaS 敏捷導入架構。在確認您的商業流程後，通常只需 2 至 4 週即可完成專屬系統建置、API 串接與追蹤代碼埋設，讓您的獲客引擎極速上線。',
    },
    {
      id: 3,
      question: '報價中的「系統架構費」與「技術保固費」包含哪些服務？',
      answer:
        '「系統架構費」是我們為您梳理商業流程、進行深度參數設定與 API 串接的單次建置費用。「技術保固費」則涵蓋了企業級雲端主機的每月運行成本、系統安全防護與技術問題排除，確保您的自動化漏斗 24 小時穩定運作。',
    },
    {
      id: 4,
      question: '你們的「廣告合規審查」服務，具體如何保護我的企業？',
      answer:
        '現在 Meta、Google 等廣告平台的政策審查極度嚴格。我們具備高度的平台政策敏銳度，會在廣告上線前，主動為您過濾容易觸發違規的高風險行銷用語。這能大幅降低您的企業粉絲專頁或廣告帳號被停權的風險，確保數位資產安全。',
    },
    {
      id: 5,
      question: '如何確保系統獲取的客戶名單品質？',
      answer:
        '我們透過「多層次意願驗證表單」與「精準受眾鎖定」雙管齊下。在前端透過高摩擦力的引流設計過濾無效客源；後端系統則利用 API 於 1 秒內將「合格商機 (SQL)」直接派發給您的業務團隊，大幅提升最終成交率並節省溝通成本。',
    },
    {
      id: 6,
      question: '「系統建置」和「廣告代操」可以分開找不同公司做嗎？',
      answer:
        '傳統做法常導致數據斷層與極高的溝通成本。全鏈獲客整合兩者的優勢在於：在系統建置初期，我們就將「廣告追蹤代碼 (GTM/Pixel)」深植於系統底層。這讓後續的廣告投放能精準捕捉流量，並直接在您的專屬後台轉化為清晰的名單，真正實現 ROI 最大化的閉環。',
    },
    {
      id: 7,
      question: '你們與一般的廣告代操公司有什麼不同？',
      answer:
        '一般代操公司只負責在前端投遞廣告，往往造成「流量看似很高，但業務覺得名單很爛」的斷層。我們是「數位系統與獲客流量」的雙棲戰隊，從內部管理防呆系統到外部追蹤代碼埋設一手包辦，讓每一分廣告預算都能在專屬系統內被精準追蹤、過濾與轉化。',
    },
    {
      id: 8,
      question: '為什麼你們的 O2O 商品展間，是與形象官網分開的獨立系統？',
      answer:
        '這是為了提供最頂尖的使用者體驗與數據安全。傳統將龐大商品庫存直接塞在官網肚子的做法，會導致手機版載入極度緩慢，且容易出現滑動卡頓的「雙重滾動」地雷。全鏈行銷採用標準的「前後端分離雙核架構」，讓形象官網專注於行銷引流；而獨立的 Web App 展間則確保商品展示流暢、防呆管理穩定。客戶甚至能將您的展間直接加入手機主畫面，達到真正的 O2O 留客閉環。',
    },
  ];

  useEffect(() => {
    document.title = '常見問題 - 全鏈獲客整合行銷 | O2O 數位系統與廣告代操 FAQ';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', '全鏈獲客整合行銷常見問題解答：O2O 系統模組適用產業、建置時程、系統架構費說明、廣告合規保護、名單品質保證、系統與廣告整合優勢，及與一般代操公司差異等 7 大企業主最常詢問問題。');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/faq`);

    const scriptId = 'jsonld-faq';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify([
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首頁', item: import.meta.env.VITE_SITE_URL || 'https://example.com' },
            { '@type': 'ListItem', position: 2, name: '常見問題', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/faq` },
          ],
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/faq#faqpage`,
          url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/faq`,
          name: '常見問題 - 全鏈獲客整合行銷',
          inLanguage: 'zh-TW',
          mainEntity: faqData.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        },
      ]);
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
      const desc2 = document.querySelector('meta[name="description"]');
      if (desc2) desc2.setAttribute('content', '全鏈獲客整合行銷為高總價 B2B、電商與實體店家打造全自動印鈔漏斗。透過痛點影音引流、精準廣告投放與 API 自動化系統串接，量身打造防禦型或極速轉換漏斗，極大化廣告投報率 ROAS。');
      const canonical2 = document.querySelector('link[rel="canonical"]');
      if (canonical2) canonical2.setAttribute('href', import.meta.env.VITE_SITE_URL || 'https://example.com');
    };
  }, []);

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar onConsultClick={handleCTA} />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-bold text-[#1E3A8A] mb-6">
            企業主常見問題
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            我們整理了企業主最常詢問的問題，幫助您快速了解全鏈獲客整合行銷的服務模式與核心價值
          </p>
        </div>
      </section>

      {/* FAQ Accordion Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="space-y-4">
            {faqData.map((item, index) => (
              <div
                key={item.id}
                className="border border-gray-200 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg"
              >
                <button
                  onClick={() => toggleAccordion(index)}
                  className="w-full px-8 py-6 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <h3 className="text-lg font-semibold text-gray-800 text-left pr-4">
                    {item.question}
                  </h3>
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                    <i
                      className={`ri-arrow-down-s-line text-2xl text-[#1E3A8A] transition-transform duration-300 ${
                        openIndex === index ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </div>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-[500px]' : 'max-h-0'
                  }`}
                >
                  <div className="px-8 py-6 bg-gray-50 border-t border-gray-200">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Help Section */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-white rounded-2xl shadow-lg p-12">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6">
              <i className="ri-question-answer-line text-5xl text-[#1E3A8A]"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              還有其他問題？
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              我們的專業顧問團隊隨時準備為您解答任何關於企業獲客與行銷漏斗的疑問
            </p>
            <button
              onClick={handleCTA}
              className="bg-[#1E3A8A] text-white px-10 py-4 rounded-full text-lg font-semibold hover:bg-[#152d6b] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              立即預約專業諮詢
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#1E3A8A] to-[#2563EB]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            準備好啟動您的專屬獲客漏斗了嗎？
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            讓我們協助您建立真正有效的企業級行銷系統，告別無效流量與問問哥
          </p>
          <button
            onClick={handleCTA}
            className="bg-white text-[#1E3A8A] px-12 py-4 rounded-full text-lg font-bold hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer"
          >
            填寫線上評估表單
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}